import React from 'react';
import Input from '../../Components/Input';
import { Image, Trash2, Video } from '../../Components/Icon';
import { BuilderActionType, BuilderContext } from './state';
import BackgroundSelector from './BackgroundSelector';
import MediaSelector from './MediaSelector';
import VendexElement, {
    VendexImageMeta,
    VendexManagedBackground,
    VendexVideoMeta,
} from '../../Model/VendexElement';
import {
    BuilderMetaPill,
    BuilderPanel,
    BuilderPillButton,
} from './BuilderPanel';
import { BuilderField, BuilderFieldGrid } from './BuilderFields';
import {
    getManagedBackgroundElementId,
    isManagedBackgroundElement,
} from './validation';

const DEFAULT_BACKGROUND: VendexManagedBackground = {
    elementType: 'IMAGE',
    metaData: {
        type: 'STATIC',
        source: '',
    },
};

const DEFAULT_VIDEO_META: VendexVideoMeta = {
    type: 'STATIC',
    source: '',
    muted: true,
    autoplay: true,
    loop: true,
};

const getBackgroundDraft = (
    element?: VendexElement,
): VendexManagedBackground => {
    if (!element?.metaData) {
        return DEFAULT_BACKGROUND;
    }

    if (element.type === 'VIDEO') {
        return {
            elementType: 'VIDEO',
            metaData: {
                ...DEFAULT_VIDEO_META,
                ...(element.metaData as VendexVideoMeta),
            },
        };
    }

    const imageMeta = element.metaData as VendexImageMeta;

    return {
        elementType: 'IMAGE',
        metaData: {
            type: imageMeta.type ?? 'STATIC',
            source: imageMeta.source ?? '',
        },
    };
};

function PageBackgroundEditor() {
    const { previewPage } = React.useContext(BuilderContext).state;
    const { dispatch } = React.useContext(BuilderContext);
    const [draft, setDraft] =
        React.useState<VendexManagedBackground>(DEFAULT_BACKGROUND);

    const backgroundElement = React.useMemo(() => {
        if (!previewPage) {
            return undefined;
        }
        return previewPage.elements.find((element) =>
            isManagedBackgroundElement(previewPage.id, element),
        );
    }, [previewPage]);

    React.useEffect(() => {
        setDraft(getBackgroundDraft(backgroundElement));
    }, [backgroundElement]);

    if (!previewPage) {
        return null;
    }

    const updateBackgroundDraft = (value: VendexManagedBackground) => {
        setDraft(value);
        dispatch({
            type: BuilderActionType.UPSERT_PAGE_BACKGROUND,
            payload: value,
        });
    };

    const managedBackgroundId =
        backgroundElement?.id ?? getManagedBackgroundElementId(previewPage.id);

    const updateMediaKind = (elementType: 'IMAGE' | 'VIDEO') => {
        if (elementType === draft.elementType) {
            return;
        }

        updateBackgroundDraft(
            elementType === 'IMAGE'
                ? {
                      elementType,
                      metaData: {
                          type: draft.metaData.type,
                          source: draft.metaData.source,
                      },
                  }
                : {
                      elementType,
                      metaData: {
                          ...DEFAULT_VIDEO_META,
                          type: draft.metaData.type,
                          source: draft.metaData.source,
                      },
                  },
        );
    };

    const updateSourceType = (type: 'STATIC' | 'DYNAMIC') => {
        if (draft.elementType === 'VIDEO') {
            updateBackgroundDraft({
                elementType: 'VIDEO',
                metaData: {
                    ...draft.metaData,
                    type,
                },
            });
            return;
        }

        updateBackgroundDraft({
            elementType: 'IMAGE',
            metaData: {
                ...draft.metaData,
                type,
            },
        });
    };

    const updateSource = (source: string) => {
        if (draft.elementType === 'VIDEO') {
            updateBackgroundDraft({
                elementType: 'VIDEO',
                metaData: {
                    ...draft.metaData,
                    source,
                },
            });
            return;
        }

        updateBackgroundDraft({
            elementType: 'IMAGE',
            metaData: {
                ...draft.metaData,
                source,
            },
        });
    };

    const updateVideoOption = (
        name: 'muted' | 'autoplay' | 'loop',
        value: boolean,
    ) => {
        if (draft.elementType !== 'VIDEO') {
            return;
        }

        updateBackgroundDraft({
            elementType: 'VIDEO',
            metaData: {
                ...draft.metaData,
                [name]: value,
            },
        });
    };

    return (
        <BuilderPanel
            title="Page background"
            icon={<Image className="h-4 w-4" />}
            description="Use a managed full-screen media element for page backgrounds. The builder keeps it pinned as the first page element."
            className="mt-4"
            actions={<BuilderMetaPill>{managedBackgroundId}</BuilderMetaPill>}
            bodyClassName="space-y-4"
        >
            <div className="grid gap-4 xl:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Background color
                    </p>
                    <div className="mt-3">
                        <BackgroundSelector
                            selector={`#${previewPage.id}`}
                            hasImageSelection={false}
                        />
                    </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Background media
                        </p>
                        {backgroundElement && (
                            <BuilderPillButton
                                tone="danger"
                                onClick={() =>
                                    dispatch({
                                        type: BuilderActionType.DELETE_PAGE_BACKGROUND,
                                    })
                                }
                                icon={<Trash2 className="h-4 w-4" />}
                            >
                                Remove
                            </BuilderPillButton>
                        )}
                    </div>
                    <BuilderFieldGrid className="mt-4">
                        <BuilderField label="Media">
                            <div className="flex flex-wrap gap-2">
                                <BuilderPillButton
                                    active={draft.elementType === 'IMAGE'}
                                    onClick={() => updateMediaKind('IMAGE')}
                                    icon={<Image className="h-4 w-4" />}
                                >
                                    Image
                                </BuilderPillButton>
                                <BuilderPillButton
                                    active={draft.elementType === 'VIDEO'}
                                    onClick={() => updateMediaKind('VIDEO')}
                                    icon={<Video className="h-4 w-4" />}
                                >
                                    Video
                                </BuilderPillButton>
                            </div>
                        </BuilderField>
                        <BuilderField label="Source mode">
                            <div className="flex flex-wrap gap-2">
                                <BuilderPillButton
                                    active={draft.metaData.type === 'STATIC'}
                                    onClick={() => updateSourceType('STATIC')}
                                >
                                    Static
                                </BuilderPillButton>
                                <BuilderPillButton
                                    active={draft.metaData.type === 'DYNAMIC'}
                                    onClick={() => updateSourceType('DYNAMIC')}
                                >
                                    Dynamic
                                </BuilderPillButton>
                            </div>
                        </BuilderField>
                        <BuilderField
                            label="Source"
                            className="md:col-span-2"
                            helperText={
                                draft.metaData.type === 'STATIC'
                                    ? `Choose the ${draft.elementType.toLowerCase()} asset to place behind this page.`
                                    : 'Provide the data key that resolves the background asset at runtime.'
                            }
                        >
                            {draft.metaData.type === 'STATIC' ? (
                                <MediaSelector
                                    url={draft.metaData.source}
                                    onChange={updateSource}
                                    type={draft.elementType}
                                />
                            ) : (
                                <Input
                                    label=""
                                    value={draft.metaData.source}
                                    onChange={(e) =>
                                        updateSource(e.target.value)
                                    }
                                    placeholder="pageBackground"
                                />
                            )}
                        </BuilderField>
                        {draft.elementType === 'VIDEO' && (
                            <>
                                <BuilderField label="Muted">
                                    <div className="flex flex-wrap gap-2">
                                        <BuilderPillButton
                                            active={draft.metaData.muted}
                                            onClick={() =>
                                                updateVideoOption('muted', true)
                                            }
                                        >
                                            Yes
                                        </BuilderPillButton>
                                        <BuilderPillButton
                                            active={!draft.metaData.muted}
                                            onClick={() =>
                                                updateVideoOption(
                                                    'muted',
                                                    false,
                                                )
                                            }
                                        >
                                            No
                                        </BuilderPillButton>
                                    </div>
                                </BuilderField>
                                <BuilderField label="Autoplay">
                                    <div className="flex flex-wrap gap-2">
                                        <BuilderPillButton
                                            active={draft.metaData.autoplay}
                                            onClick={() =>
                                                updateVideoOption(
                                                    'autoplay',
                                                    true,
                                                )
                                            }
                                        >
                                            Yes
                                        </BuilderPillButton>
                                        <BuilderPillButton
                                            active={!draft.metaData.autoplay}
                                            onClick={() =>
                                                updateVideoOption(
                                                    'autoplay',
                                                    false,
                                                )
                                            }
                                        >
                                            No
                                        </BuilderPillButton>
                                    </div>
                                </BuilderField>
                                <BuilderField label="Loop">
                                    <div className="flex flex-wrap gap-2">
                                        <BuilderPillButton
                                            active={draft.metaData.loop}
                                            onClick={() =>
                                                updateVideoOption('loop', true)
                                            }
                                        >
                                            Yes
                                        </BuilderPillButton>
                                        <BuilderPillButton
                                            active={!draft.metaData.loop}
                                            onClick={() =>
                                                updateVideoOption('loop', false)
                                            }
                                        >
                                            No
                                        </BuilderPillButton>
                                    </div>
                                </BuilderField>
                            </>
                        )}
                    </BuilderFieldGrid>
                </div>
            </div>
        </BuilderPanel>
    );
}

export default PageBackgroundEditor;
